import { useEffect } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { organizationService } from '@services/organizationService';
import { toDisplayError } from '../../utils/apiError';

const inviteSchema = z.object({
  email: z.string().min(1, 'auth.validation.emailRequired').email('auth.validation.emailInvalid'),
  role: z.enum(['admin', 'manager', 'member', 'viewer']),
});

const INVITE_ROLES = ['admin', 'manager', 'member', 'viewer'] as const;

type InviteFormData = z.infer<typeof inviteSchema>;

interface InviteMemberDialogProps {
  open: boolean;
  orgId: string;
  onClose: () => void;
  /** Called after a successful invite so the host can refresh its list. */
  onInvited?: () => void;
}

/**
 * Shared invite dialog used by both the Members and Invitations tabs so the
 * "Invite member" action opens it in place rather than navigating away
 * (REV-401). Owns its own form + request state.
 */
function InviteMemberDialog({ open, orgId, onClose, onInvited }: InviteMemberDialogProps) {
  const { t } = useTranslation();
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { email: '', role: 'member' },
  });

  // Reset transient state whenever the dialog is (re)opened.
  useEffect(() => {
    if (open) {
      setInviteError(null);
      setInviteSuccess(false);
      reset();
    }
  }, [open, reset]);

  const handleInvite = async (data: InviteFormData) => {
    setInviting(true);
    setInviteError(null);
    setInviteSuccess(false);
    try {
      await organizationService.createInvitation(orgId, { email: data.email, role: data.role });
      setInviteSuccess(true);
      reset();
      onInvited?.();
      setTimeout(() => {
        setInviteSuccess(false);
        onClose();
      }, 1500);
    } catch (err) {
      setInviteError(toDisplayError(err, t('organizations.invitations.inviteFailed')));
    } finally {
      setInviting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !inviting && onClose()} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(handleInvite)}>
        <DialogTitle>{t('organizations.invite.title')}</DialogTitle>
        <DialogContent>
          {inviteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {inviteError}
            </Alert>
          )}
          {inviteSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('organizations.invitations.sent')}
            </Alert>
          )}
          <TextField
            {...register('email')}
            label={t('organizations.invite.emailLabel')}
            placeholder={t('organizations.invite.emailPlaceholder')}
            fullWidth
            margin="normal"
            error={!!errors.email}
            helperText={errors.email ? t(errors.email.message as string) : undefined}
            disabled={inviting || inviteSuccess}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="invite-role-label" shrink>
              {t('organizations.invite.roleLabel')}
            </InputLabel>
            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  labelId="invite-role-label"
                  label={t('organizations.invite.roleLabel')}
                  disabled={inviting || inviteSuccess}
                  notched
                  onChange={(e: SelectChangeEvent) => field.onChange(e.target.value)}
                >
                  {INVITE_ROLES.map((slug) => (
                    <MenuItem key={slug} value={slug}>
                      {t(`organizations.roles.${slug}`)}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={inviting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={inviting || inviteSuccess}>
            {inviting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('organizations.invite.submit')
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default InviteMemberDialog;
